import { Box, DataList, Heading } from "@radix-ui/themes";
import { useMemo } from "react";
import { selectSelectedCameraSkuSection } from "@/entities/planogram";
import { updateVideoStream } from "@/entities/player";
import { SkuSection } from "@/shared/api";
import { useAppDispatch, useAppSelector } from "@/app/appStore";
import { Projections } from "./projections";
import { SkuSectionInfo } from "./sku-section-info";
import { getStoreImageSrc } from "./helpers/getImageSrc";

export function Sidebar({
  gondolaSkuSections,
}: {
  gondolaSkuSections?: Record<string, SkuSection>[];
}) {
  const dispatch = useAppDispatch();
  const selectedCamera = useAppSelector(
    (state) => state.player.videoStream.cameraId
  );
  const selectedCameraSkuSection = useAppSelector(
    selectSelectedCameraSkuSection
  );
  const totalSkuSectionCount = gondolaSkuSections?.length || 0;

  const projections = useMemo(() => {
    const skuSectionId = selectedCameraSkuSection?.sku_section_id;

    const cameras = gondolaSkuSections?.find(
      (skuSections) =>
        Object.values(skuSections)[0].sku_section_id === skuSectionId
    );

    if (cameras) {
      return Object.entries(cameras).map(([cameraId, skuSection]) => {
        return {
          image: {
            compressed: getStoreImageSrc(skuSection.compressed_image),
            original: getStoreImageSrc(skuSection.original_image),
            width: skuSection.width,
            height: skuSection.height,
          },
          cameraId,
          cameraSkuSection: skuSection,
        };
      });
    }

    return [];
  }, [selectedCameraSkuSection, gondolaSkuSections]);

  return (
    <aside
      id="sidebar"
      className="min-w-[240px] w-[240px] border-l border-[var(--gray-7)]"
    >
      {selectedCameraSkuSection ? (
        <SkuSectionInfo
          selectedCameraSkuSection={selectedCameraSkuSection}
          renderProjections={
            <Projections
              value={selectedCamera || ""}
              projections={projections}
              getIsSelected={({ cameraId }) => selectedCamera === cameraId}
              onProjectionClick={({ cameraId }) => {
                dispatch(updateVideoStream({ cameraId }));
              }}
            />
          }
        />
      ) : (
        <GondolaInfo totalSkuSectionCount={totalSkuSectionCount} />
      )}
    </aside>
  );
}

function GondolaInfo({
  totalSkuSectionCount,
}: {
  totalSkuSectionCount: number;
}) {
  const selectedGondola = useAppSelector(
    (state) => state.planogram.selectedGondola
  );
  const shelfTrackingCameras = selectedGondola?.cameras.filter(
    (camera) => camera.cameraType === "shelf_tracking"
  );
  const imageDetails = {
    width: shelfTrackingCameras?.[0]?.width || 0,
    height: shelfTrackingCameras?.[0]?.height || 0,
  };

  return (
    <Box py="2" className="grow">
      <Heading size="2" mx="2">
        Gondola info
      </Heading>
      <Box p="2">
        <DataList.Root size="1" className="gap-1">
          <DataList.Item>
            <DataList.Label>ID:</DataList.Label>
            <DataList.Value>{selectedGondola?.id}</DataList.Value>
          </DataList.Item>
          <DataList.Item>
            <DataList.Label>Gondola ref name</DataList.Label>
            <DataList.Value>-</DataList.Value>
          </DataList.Item>
          <DataList.Item>
            <DataList.Label>Gondola name</DataList.Label>
            <DataList.Value>{selectedGondola?.name}</DataList.Value>
          </DataList.Item>
          <DataList.Item>
            <DataList.Label>Total SKU Sections</DataList.Label>
            <DataList.Value>{totalSkuSectionCount}</DataList.Value>
          </DataList.Item>
          <DataList.Item>
            <DataList.Label>Cameras involved</DataList.Label>
            <DataList.Value>
              {shelfTrackingCameras?.map((camera) => camera.id).join(", ") || 0}
            </DataList.Value>
          </DataList.Item>
          <DataList.Item>
            <DataList.Label>Image resolution</DataList.Label>
            <DataList.Value>
              {imageDetails.width}x{imageDetails.height}
            </DataList.Value>
          </DataList.Item>
        </DataList.Root>
      </Box>
    </Box>
  );
}
